import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PublicLayout } from '@/components/public';
import { t } from '@/i18n';
import { 
  ArrowRight, Shield, Users, Award, Target, Heart, 
  CheckCircle, Building2, Scale, Clock
} from 'lucide-react';

const AboutPage = () => {
  const navigate = useNavigate();

  const values = [
    {
      icon: Shield,
      title: t("Trust & Integrity"),
      description: t("We handle your critical legal documents with the utmost care and accuracy. Your lien rights are our priority."),
    },
    {
      icon: Users,
      title: t("Customer First"),
      description: t("Our dedicated team provides personalized support to ensure your success. We're here when you need us."),
    },
    {
      icon: Target,
      title: t("Accuracy"),
      description: t("Every notice is reviewed for compliance and accuracy. We ensure your documents meet state requirements."),
    },
    {
      icon: Clock,
      title: t("Reliability"),
      description: t("Fast turnaround times and consistent service you can count on, project after project."),
    },
  ];

  const stats = [
    { value: '1M+', label: t("Notices Sent") },
    { value: '$10B+', label: t("Money Secured") },
    { value: '50', label: t("States Covered") },
    { value: '1000s', label: t("Happy Customers") },
  ];

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-[#1e3a5f] via-[#2d4a6f] to-[#1e3a5f]" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <Badge className="bg-[#F97316] text-white mb-4">{t("About Us")}</Badge>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4">
                {t("Our Business is")}
                <span className="text-[#F97316]"> {t("Getting You Paid")}</span>
              </h1>
              <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                {t("All American Notice was founded with a simple mission: to help construction professionals protect their lien rights and secure the money they've rightfully earned.")}
              </p>
              <Button 
                size="lg" 
                onClick={() => navigate('/register')} 
                className="bg-[#F97316] hover:bg-[#ea6a10] text-white font-semibold px-8"
              >
                {t("Get Started")} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
            <div className="hidden lg:block">
              <img 
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w0NjI2NjZ8MHwxfHNlYXJjaHwyfHx0ZWFtJTIwb2ZmaWNlfGVufDB8fHx8MTY5NDc2NDA3Mnww&ixlib=rb-4.0.3&q=85&w=600"
                alt={t("Our team")}
                className="rounded-2xl shadow-2xl border-4 border-white/10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-3xl lg:text-4xl font-black text-[#F97316]">{stat.value}</p>
                <p className="text-slate-600 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 lg:py-20 bg-slate-50" data-testid="our-story">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-[#1e3a5f] text-white mb-4">{t("Our Story")}</Badge>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1e3a5f] mb-6">
                {t("Built by Construction Industry Professionals")}
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  {t("We understand the challenges of the construction industry because we've lived them. Unpaid invoices, complex lien laws, and missed deadlines cost contractors billions of dollars every year.")}
                </p>
                <p>
                  {t("All American Notice was created to solve this problem. Our platform combines industry expertise with modern technology to make protecting your lien rights simple, affordable, and reliable.")}
                </p>
                <p>
                  {t("Whether you're a general contractor, subcontractor, or material supplier, we're here to help you get paid for the work you do.")}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                  <Scale className="h-8 w-8 text-[#F97316] mb-3" />
                  <h3 className="font-semibold text-[#1e3a5f]">{t("Legal Compliance")}</h3>
                  <p className="text-sm text-slate-600">{t("State-specific documents")}</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                  <Building2 className="h-8 w-8 text-[#F97316] mb-3" />
                  <h3 className="font-semibold text-[#1e3a5f]">{t("All 50 States")}</h3>
                  <p className="text-sm text-slate-600">{t("Nationwide coverage")}</p>
                </div>
              </div>
              <div className="space-y-4 mt-8">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                  <Clock className="h-8 w-8 text-[#F97316] mb-3" />
                  <h3 className="font-semibold text-[#1e3a5f]">{t("Fast Service")}</h3>
                  <p className="text-sm text-slate-600">{t("24-48 hour turnaround")}</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                  <Heart className="h-8 w-8 text-[#F97316] mb-3" />
                  <h3 className="font-semibold text-[#1e3a5f]">{t("Dedicated Support")}</h3>
                  <p className="text-sm text-slate-600">{t("Real humans, real help")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 lg:py-20 bg-white" data-testid="our-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-[#F97316] text-white mb-4">{t("Our Values")}</Badge>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1e3a5f] mb-4">
              {t("What Drives Us Every Day")}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t("Our core values guide everything we do, from document preparation to customer support.")}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card key={index} className="border-slate-200 hover:shadow-lg hover:border-[#1e3a5f]/30 transition-all text-center">
                <CardContent className="p-6">
                  <div className="w-14 h-14 rounded-xl bg-[#1e3a5f]/10 flex items-center justify-center mx-auto mb-4">
                    <value.icon className="h-7 w-7 text-[#F97316]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#1e3a5f] mb-2">{value.title}</h3>
                  <p className="text-slate-600 text-sm">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-[#1e3a5f] text-white mb-4">{t("Why Choose Us")}</Badge>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1e3a5f] mb-6">
                {t("The All American Notice Difference")}
              </h2>
              <ul className="space-y-4">
                {[
                  t("No monthly fees or subscriptions - pay only for what you use"),
                  t("Notices from $35 – no subscription, no setup fee"),
                  t("Expert research team finds all required recipients"),
                  t("State-compliant documents reviewed for accuracy"),
                  t("Real USPS certified mail with tracking"),
                  t("Dedicated customer support when you need help"),
                  t("Secure online dashboard for all your documents"),
                  t("Automatic deadline tracking and reminders"),
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
              <div className="text-center">
                <Award className="h-16 w-16 text-[#F97316] mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-[#1e3a5f] mb-2">{t("Trusted Nationwide")}</h3>
                <p className="text-slate-600 mb-6">
                  {t("Thousands of general contractors, subcontractors, and suppliers trust All American Notice to protect their lien rights.")}
                </p>
                <div className="grid grid-cols-3 gap-4 text-center border-t border-slate-200 pt-6">
                  <div>
                    <p className="text-2xl font-bold text-[#F97316]">1M+</p>
                    <p className="text-xs text-slate-500">{t("Notices")}</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#F97316]">50</p>
                    <p className="text-xs text-slate-500">{t("States")}</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#F97316]">98%</p>
                    <p className="text-xs text-slate-500">{t("Satisfaction")}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-20 bg-[#1e3a5f]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
            {t("Ready to Work With Us?")}
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            {t("Join thousands of construction professionals who trust All American Notice to protect their lien rights.")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={() => navigate('/register')} 
              className="bg-[#F97316] hover:bg-[#ea6a10] text-white text-lg px-10 h-14 font-semibold"
            >
              {t("Get Started")} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => navigate('/contact')} 
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#1e3a5f] text-lg px-10 h-14"
            >
              {t("Contact Us")}
            </Button>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default AboutPage;
